import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslate } from "../../../../hooks/useTranslate";
import { useRangeRefinements } from "../rangeQuestionComponents/hooks/useRangeRefinements";
import {
  MeasurementUnitsToString,
  qualifierValueToQualifierFragment,
} from "../../../../utils/dataHelpers";
import {
  ComplexMeasurementUnits,
  ConversationNextQuestionSuggestions,
  MeasurementUnits,
  Qualifier_Range,
  QualifierFragment,
} from "fe-common/graphql";
import { NumericFeatureIds } from "../rangeIds";
import { CurrentUnitConverter } from "./logic(controllerHooks)/useUnitConveter";

// Usually ranges have low,normal and high, to get the average value, get the
// middle of the normal range - we use the id of NORMAL to do that
const NORMAL_RANGE_ID = "FE.74aa59af-e13c-4a5b-8b74-5f196c7cea64";
// We have some hard-codded normal average, ie for blood pressure
const DIASTOLIC_NORMAL_AVERAGE = 60;
const SYSTEOLY_NORMAL_AVERAGE = 120;

export interface NumericInputControllerProps {
  suggestion: ConversationNextQuestionSuggestions;
  // NOTE: this has to be wrapped in useCallback since it's a dependency in a useEffect
  onValidValue: (QualifiedFeature, index?: number) => void;
  index?: number;
  currentUnitConverter?: CurrentUnitConverter;
  // Some units can have long fixed point, default is 2 numbers after the point
  allowedFixPoint?: number;
}

export interface NumericInputControllerApi {
  setValue: (num: number | string) => void;
  value: number;
  stringValue: string;
  errorText: string;
  maxValue: number;
  minValue: number;
  handleValueChanged: (number) => void;
  rangeUnits: MeasurementUnits;
  complexUnits: ComplexMeasurementUnits;
  rangeItems: QualifierFragment[];
  // Find the range of "NORMAL" and return it's average
  normalAverage: number;
  normalRange: Qualifier_Range;
  validateValue: (number) => void;
}

const identityConverter = (i) => i;

const DefaultUnitConverter: CurrentUnitConverter = {
  unit: null,
  converters: null,
  displayText: "default",
  convertFromRange: identityConverter,
  fromOldToNew: (i) => i,
};

// Don't show decimal point if we don't need it
const numberToString = (newNumber: number, showInFeet = false) => {
  if (isNaN(newNumber) || newNumber === null || newNumber === undefined)
    return "";
  if (showInFeet) {
    const feet = Math.floor(newNumber / 12);
    const inches = numberToString(newNumber % 12, false);
    return (feet >= 1 ? `${feet}'` : "") + `${inches}"`;
  }
  return Math.floor(newNumber) === newNumber
    ? newNumber.toFixed(0)
    : newNumber.toFixed(2);
};

export const useNumericInputController = ({
  suggestion,
  onValidValue,
  index,
  currentUnitConverter = DefaultUnitConverter,
  allowedFixPoint = 2,
}: NumericInputControllerProps): NumericInputControllerApi => {
  // Thats the actual parsed value that we are going to use
  const [value, setNumericValue] = useState<number>();

  // That's the value that will be displayed in the input
  const [stringValue, setStringValue] = useState<string>("");

  const { translate } = useTranslate();

  const [errorText, setError] = useState<string>(null);

  const { rangeItems, relation, abstractSuggestion } = useRangeRefinements({
    suggestion,
  });

  const maxValue = useMemo(
    () =>
      rangeItems.reduce(
        (max, current) => Math.max(current.range?.high || 0, max),
        Number.MIN_VALUE,
      ),
    [rangeItems],
  );

  const minValue = useMemo(
    () =>
      rangeItems.reduce(
        (min, current) => Math.min(current.range?.low, min),
        Number.MAX_VALUE,
      ),
    [rangeItems],
  );

  const normalRange = useMemo(
    () => rangeItems.find((r) => r.value.id === NORMAL_RANGE_ID)?.range,
    [rangeItems],
  );

  // Sometimes we ger complex units for basic units (with nominator length 1 and without denominator)
  const rangeUnits = useMemo(() => {
    const u =
      normalRange?.units ||
      (normalRange?.complexUnits?.denominator?.length &&
        normalRange.complexUnits?.numerator?.length !== 1)
        ? normalRange?.units
        : normalRange?.complexUnits?.numerator[0];
    return u;
  }, [normalRange]);

  const complexUnits = normalRange?.complexUnits;

  const normalAverage = useMemo(() => {
    if (
      suggestion.suggestion.feature.id ===
      NumericFeatureIds.DiastolicBloodPressure
    )
      return DIASTOLIC_NORMAL_AVERAGE;
    if (
      suggestion.suggestion.feature.id ===
      NumericFeatureIds.SystolicBloodPressure
    )
      return SYSTEOLY_NORMAL_AVERAGE;
    if (!normalRange) return (minValue + maxValue) / 2;
    return (normalRange?.low + normalRange?.high) / 2;
  }, [suggestion, minValue, maxValue, normalRange]);

  const buildAnswer = useCallback(
    (_value) => {
      const numericValue = Number.parseFloat(_value);

      // Find the suitable qualifier to send
      const qualifierValue = rangeItems.find(
        (q) =>
          currentUnitConverter.convertFromRange(q.range?.low || 0) <=
            numericValue &&
          currentUnitConverter.convertFromRange(q.range?.high || 0) >=
            numericValue,
      );
      // If we don't have any, something is wrong, send null
      if (!qualifierValue) return null;

      const answerQualifier = {
        ...qualifierValue.value,
        range: {
          low: numericValue,
          high: numericValue,
          units:
            currentUnitConverter.unit || qualifierValue.range.complexUnits
              ? null
              : qualifierValue.range?.units ||
                qualifierValue.value?.range?.units ||
                MeasurementUnits.Units,
          complexUnits: currentUnitConverter.unit
            ? { numerator: [currentUnitConverter.unit], denominator: [] }
            : qualifierValue.range?.complexUnits,
        },
      };

      return {
        ...abstractSuggestion,
        qualifiers: [
          qualifierValueToQualifierFragment(answerQualifier, relation, true),
        ],
      };
    },
    [abstractSuggestion, rangeItems, relation, currentUnitConverter],
  );

  const validateValue = useCallback(
    (_value: string) => {
      try {
        const numberValue = Number.parseFloat(_value);

        const convertedMinValue = currentUnitConverter.convertFromRange(
          minValue,
        );

        const convertedMaxValue = currentUnitConverter.convertFromRange(
          maxValue,
        );

        if (
          numberValue < convertedMinValue ||
          numberValue > convertedMaxValue
        ) {
          setError(
            `${translate("VALUES_CAN_BE_BETWEEN")} ${numberToString(
              convertedMinValue,
              currentUnitConverter.unit === MeasurementUnits.Inches,
            )} ${translate("AND")} ${numberToString(
              convertedMaxValue,
              currentUnitConverter.unit === MeasurementUnits.Inches,
            )} ${MeasurementUnitsToString(
              currentUnitConverter.unit || rangeUnits,
            )}`,
          );
        } else {
          // Everytime there is a valid value in the input, construct a valid answer
          setError(null);
        }
      } catch (ex) {
        setError(ex["message"]);
      }
    },
    [setError, maxValue, minValue, translate, currentUnitConverter, rangeUnits],
  );

  // Make sure to validate the value once units change to get updated error
  useEffect(() => {
    validateValue(numberToString(value) || "");
  }, [rangeUnits, validateValue, value, buildAnswer]);

  // Make sure we dispose of the value is there is error
  // or notify with the right value once the error is cleared
  useEffect(() => {
    if (errorText && errorText !== "") {
      onValidValue?.(null, index);
      return;
    }
    const answer_value = buildAnswer(value);
    onValidValue?.(answer_value, index);
  }, [errorText, value, buildAnswer, onValidValue, index]);

  const handleValueChanged = useCallback(
    (comaSplitedValue) => {
      const _value = comaSplitedValue?.replace?.(",", ".") || comaSplitedValue;
      setStringValue(_value);
      setNumericValue((oldValue) => {
        // Avoid cyclic updates if the same value was validated already
        if (oldValue === _value) return oldValue;
        const parsedValue =
          parseFloat((parseFloat(_value) || 0).toFixed(allowedFixPoint)) ||
          null;
        validateValue(_value);
        return parsedValue;
      });
    },
    [setNumericValue, validateValue, allowedFixPoint],
  );

  // A public function to change the value (make sure to update both value states)
  const setValue = (newNumber: number | string) => {
    if (typeof newNumber === "string") {
      setNumericValue(parseFloat(newNumber));
      setStringValue(newNumber);
      return;
    }
    setNumericValue(parseFloat(numberToString(newNumber)));
    setStringValue(numberToString(newNumber));
  };

  // Make sure we update the value if the units change
  useEffect(
    () =>
      setNumericValue((old) => {
        // Make sure we don't run old to new on null
        const numValue = !old ? old : currentUnitConverter?.fromOldToNew?.(old);
        setStringValue(numberToString(numValue));
        return numValue;
      }),
    [currentUnitConverter, setNumericValue, setStringValue],
  );

  return {
    setValue,
    value,
    stringValue,
    errorText,
    maxValue,
    minValue,
    handleValueChanged,
    rangeUnits,
    complexUnits,
    rangeItems,
    normalAverage,
    normalRange,
    validateValue,
  };
};
